import { useCallback, useState, type ReactNode } from 'react'

import { request, requestWithMeta } from '../api/client.js'
import { ApiError } from '../api/client.js'
import { DataTable, type Column } from '../components/DataTable.tsx'

interface AuditEntry {
  readonly id: string
  readonly seq: string
  readonly eventName: string
  readonly occurredAt: string
  readonly actor: string
  readonly correlationId: string
  readonly hash: string
  readonly payload: { readonly payload?: unknown }
}

interface VerifyResult {
  readonly valid: boolean
  readonly checked: number
  readonly brokenAtIndex: number
  readonly brokenSeq: string | null
  readonly reason?: string
}

/**
 * Penelusuran catatan audit — G1-8.
 *
 * Tombol verifikasi ada di halaman ini, bukan tersembunyi di API, karena
 * rantai yang tidak pernah diverifikasi sama nilainya dengan tidak punya
 * rantai. Verifikasi harus semudah menekan satu tombol supaya benar-benar
 * dilakukan.
 */
export function AuditPage(): ReactNode {
  const [hasil, setHasil] = useState<VerifyResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const fetchPage = useCallback(async (page: number, q: string) => {
    const params = new URLSearchParams({ page: String(page), limit: '25' })
    // Pencarian bebas belum didukung server; kolom `eventName` adalah sumbu
    // penyaringan yang benar-benar berguna saat menelusuri satu jenis tindakan.
    if (q !== '') params.set('eventName', q)
    const { data, total } = await requestWithMeta<readonly AuditEntry[]>(`/api/v1/audit?${params}`)
    return { rows: data, total }
  }, [])

  async function verifikasi(): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      setHasil(await request<VerifyResult>('/api/v1/audit/verifikasi'))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Tidak dapat menghubungi server')
    } finally {
      setBusy(false)
    }
  }

  const COLUMNS: readonly Column<AuditEntry>[] = [
    { key: 'seq', label: '#', render: (e) => <code>{e.seq}</code> },
    { key: 'waktu', label: 'Waktu', render: (e) => e.occurredAt.slice(0, 19).replace('T', ' ') },
    { key: 'peristiwa', label: 'Peristiwa', render: (e) => <strong>{e.eventName}</strong> },
    { key: 'pelaku', label: 'Pelaku', render: (e) => e.actor },
    {
      key: 'muatan',
      label: 'Muatan',
      render: (e) => (
        <code className="muatan">{JSON.stringify(e.payload.payload ?? {}).slice(0, 90)}</code>
      ),
    },
  ]

  return (
    <div>
      <h1>Catatan Audit</h1>
      <p className="hint">
        Setiap entri terikat pada entri sebelumnya lewat hash. Mengubah satu entri memutus rantai
        dan terdeteksi tepat pada posisinya — tanpa perlu membandingkan dengan salinan mana pun.
      </p>

      <div className="page-actions">
        <button onClick={() => void verifikasi()} disabled={busy}>
          Verifikasi keutuhan rantai
        </button>
        {hasil !== null &&
          (hasil.valid ? (
            <span className="badge badge-diterbitkan">UTUH · {hasil.checked} entri diperiksa</span>
          ) : (
            <span className="badge badge-ditolak">
              RUSAK pada entri #{hasil.brokenSeq} — {hasil.reason ?? 'rantai tidak cocok'}
            </span>
          ))}
      </div>

      {error !== null && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      <p className="hint">Saring dengan mengetik nama peristiwa, misalnya `LoginGagal`.</p>
      <DataTable columns={COLUMNS} fetchPage={fetchPage} emptyText="Belum ada catatan." />
    </div>
  )
}
